import { lazySelector, mergeDeep } from '@sg-core/resources/assets/js/utils';

import { AnimateReveal } from './animate-reveal';
import { Delayer } from './delayer';
import { STATES } from './states';

export interface AnimateGroupRevealOptions {
  disableDelayer?: boolean;
  useObserver?: boolean;
  observerOptions?: IntersectionObserverInit;
  gsapOptions?: Record<string, unknown>;
  parentSelector?: string;
  parentElement?: Element;
  childElements?: NodeListOf<Element>;
  childrenSelector?: string;
  start?: string;
  delay?: number;
  initialDelay?: number;
  animateByRow?: boolean;
}

export class AnimateGroupReveal {
  private disableDelayer: boolean;
  private parent: Element | null;
  private children: NodeListOf<Element> | undefined;
  private start: string;
  private delay: number;
  private initialDelay: number;
  private animateByRow: boolean;
  private observer: IntersectionObserver | null = null;
  private useObserver: boolean;
  private observerOptions: IntersectionObserverInit;
  private gsapOptions: Record<string, unknown>;

  constructor(options: AnimateGroupRevealOptions = {}) {
    /*
     * In some cases we want to disable the delayer part and handle it outside
     * of this class. For example, when we want to have more control on the index element.
     */
    this.disableDelayer = options.disableDelayer ?? false;
    this.useObserver = options.useObserver ?? false;
    this.observerOptions = options.observerOptions ?? {};
    this.gsapOptions = options.gsapOptions ?? {};

    const parentSelector = options.parentSelector || options.parentElement;
    this.parent = parentSelector ? lazySelector(parentSelector) : null;

    this.children =
      options.childElements || (this.parent ? this.parent.querySelectorAll(options.childrenSelector ?? '') : undefined);

    this.start = options.start ?? (window.innerWidth > 768 ? 'top 70%' : 'top 95%');
    this.delay = options.delay ?? 0.2;
    this.initialDelay = options.initialDelay ?? 0;
    this.animateByRow = options.animateByRow ?? false;

    if (this.useObserver) {
      this.initObserver();
    }

    this.initReveal();
  }

  private initObserver(): void {
    const defaultOptions: IntersectionObserverInit = {
      threshold: 0.2,
      rootMargin: '0px',
    };

    const observerOptions: IntersectionObserverInit = {
      ...defaultOptions,
      ...this.observerOptions,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(STATES.REVEALED);
          this.observer?.unobserve(entry.target);
        }
      });
    }, observerOptions);
  }

  private initReveal(): void {
    this.children?.forEach((elem, index) => {
      const htmlElem = elem as HTMLElement;

      if (!this.disableDelayer) {
        new Delayer(htmlElem, {
          elements: this.children,
          index,
          animateByRow: this.animateByRow,
          delay: this.delay,
          initialDelay: this.initialDelay,
        });
      }

      if (this.useObserver) {
        this.observer?.observe(htmlElem);
        new AnimateReveal(htmlElem, {
          gsapOptions: {
            paused: true, // Start paused since we'll control it with intersection observer
          },
        });
      } else {
        new AnimateReveal(htmlElem, {
          gsapOptions: mergeDeep(
            {
              scrollTrigger: {
                start: this.start,
                trigger: this.animateByRow ? htmlElem : this.parent,
              },
            },
            this.gsapOptions,
          ),
        });
      }
    });
  }
}
